import { makeAutoObservable, observable, runInAction } from "mobx";
import { track, type AnalyticsSource } from "@/lib/analytics";
import { changedFields } from "@/lib/changed-fields";
import { pollExportStatus } from "@/components/data-table/use-csv-export";
import { toDateOnlyString } from "@/lib/date";
import type { ITimeOffEntriesListQuery } from "@/services/time-off.service";
import type { RootStore } from "./root.store";
import type { ITimeOffEntry, ITimeOffEntryFilters, ITimeOffEntryPayload } from "@/types/time-off";

export class TimeOffStore {
  entries: ITimeOffEntry[] = [];
  filters: ITimeOffEntryFilters = {};
  isLoading = false;
  // Last entries-load failure, surfaced to the DataTable `error` prop. Cleared
  // when a (re)fetch starts; set when fetchEntries rejects.
  loadError: Error | null = null;
  isSaving = false;
  activeEntryId: string | null = null;

  // --- Paginated settings-list cache (parallel to `entries`) -----------------
  // The settings Time Off table is server-driven (filters/sort/pagination); the
  // full `entries` list above stays what the My Time Off panel (fetchMyEntries)
  // renders.
  pagedEntries: ITimeOffEntry[] = [];
  pageInfo: { totalCount: number; hasNext: boolean; nextCursor: string | null } = {
    totalCount: 0,
    hasNext: false,
    nextCursor: null,
  };
  isPageLoading = false;
  isLoadingMore = false;
  pageError: Error | null = null;
  private lastPageQuery: ITimeOffEntriesListQuery = {};

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(
      this,
      {
        rootStore: false,
        entries: observable.ref,
        pagedEntries: observable.ref,
        lastPageQuery: false,
      } as never,
      { autoBind: true },
    );
  }

  /**
   * The caller's own entries that have not finished yet, in date order.
   *
   * `/time-off/me/` already drops finished rows (NG-26), so this is not the
   * filter — it is what keeps a *just-saved* back-dated entry from sitting in
   * the drawer, because `createMyEntry` / `updateMyEntry` splice the row
   * straight into `entries` rather than refetching. Dates are date-only ISO
   * strings, so a lexicographic compare is the date compare.
   *
   * Computed here rather than in the drawer because two things read it now: the
   * list, and the count beside the drawer's title. A second copy of the
   * predicate is a second chance for the header and the list to disagree.
   */
  get upcomingEntries(): ITimeOffEntry[] {
    const today = toDateOnlyString(new Date());
    return this.entries.filter((entry) => entry.end_date >= today).sort(compareEntries);
  }

  /** Load the first page of the settings list with the given server query
   *  (search / member / sort). Replaces the current page. */
  async loadEntriesPage(slug: string, query: ITimeOffEntriesListQuery = {}): Promise<void> {
    this.isPageLoading = true;
    this.pageError = null;
    this.lastPageQuery = query;
    try {
      const page = await this.rootStore.timeOffService.listEntriesPage(slug, {
        ...query,
        cursor: null,
      });
      runInAction(() => {
        this.pagedEntries = page.results;
        this.pageInfo = {
          totalCount: page.total_count,
          hasNext: page.next_page_results,
          nextCursor: page.next_cursor || null,
        };
      });
    } catch (err) {
      runInAction(() => {
        this.pageError = err instanceof Error ? err : new Error(String(err));
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isPageLoading = false;
      });
    }
  }

  /** Append the next page (DataTable infinite scroll). Re-runs the first page
   *  when nothing is loaded yet (the error-empty "Try again" path). */
  async loadMoreEntries(slug: string): Promise<void> {
    if (this.pagedEntries.length === 0) {
      await this.loadEntriesPage(slug, this.lastPageQuery);
      return;
    }
    if (!this.pageInfo.hasNext || !this.pageInfo.nextCursor || this.isLoadingMore) return;
    this.isLoadingMore = true;
    this.pageError = null;
    try {
      const page = await this.rootStore.timeOffService.listEntriesPage(slug, {
        ...this.lastPageQuery,
        cursor: this.pageInfo.nextCursor,
      });
      runInAction(() => {
        const seen = new Set(this.pagedEntries.map((e) => e.id));
        this.pagedEntries = [...this.pagedEntries, ...page.results.filter((e) => !seen.has(e.id))];
        this.pageInfo = {
          totalCount: page.total_count,
          hasNext: page.next_page_results,
          nextCursor: page.next_cursor || null,
        };
      });
    } catch (err) {
      runInAction(() => {
        this.pageError = err instanceof Error ? err : new Error(String(err));
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isLoadingMore = false;
      });
    }
  }

  /** Export the time-off list as CSV, scoped to the current page query and
   *  (when `ids` is non-empty) restricted to the selected rows. */
  exportEntriesCsv(slug: string, ids?: string[], columns?: string[]): Promise<Response> {
    const body: { ids?: string[]; columns?: string[] } = {};
    if (ids && ids.length > 0) body.ids = ids;
    if (columns && columns.length > 0) body.columns = columns;
    return this.rootStore.timeOffService.exportTimeOff(slug, body, {
      q: this.lastPageQuery.q,
      member: this.lastPageQuery.member,
      order_by: this.lastPageQuery.order_by,
    });
  }

  pollExport(slug: string, token: string): Promise<string | null> {
    return pollExportStatus(token, (tok) =>
      this.rootStore.timeOffService.getExportStatus(slug, tok),
    );
  }

  setFilters(filters: ITimeOffEntryFilters): void {
    this.filters = filters;
  }

  async fetchEntries(
    slug: string,
    filters: ITimeOffEntryFilters = this.filters,
  ): Promise<ITimeOffEntry[]> {
    this.isLoading = true;
    this.loadError = null;
    try {
      const entries = await this.rootStore.timeOffService.listEntries(slug, filters);
      runInAction(() => {
        this.entries = entries;
        this.filters = filters;
      });
      return entries;
    } catch (err) {
      runInAction(() => {
        this.loadError = err instanceof Error ? err : new Error(String(err));
      });
      throw err;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async fetchMyEntries(
    slug: string,
    filters: ITimeOffEntryFilters = this.filters,
  ): Promise<ITimeOffEntry[]> {
    this.isLoading = true;
    try {
      const entries = await this.rootStore.timeOffService.listMyEntries(slug, filters);
      runInAction(() => {
        this.entries = entries;
        this.filters = filters;
      });
      return entries;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  /**
   * The loaded copy of an entry, from either list, shaped like the payload an
   * update sends, for its `fields`: the payload names the person as `user_id`
   * where the entry holds a `user` object. A dialog sends every field on every
   * save, so without this the person would read as changed each time.
   */
  private beforeUpdate(entryId: string): (ITimeOffEntry & { user_id?: string }) | undefined {
    const entry =
      this.entries.find((e) => e.id === entryId) ?? this.pagedEntries.find((e) => e.id === entryId);
    return entry ? { ...entry, user_id: entry.user?.id } : undefined;
  }

  async createEntry(
    slug: string,
    data: ITimeOffEntryPayload,
    source: AnalyticsSource = "page",
  ): Promise<ITimeOffEntry> {
    this.isSaving = true;
    try {
      const entry = await this.rootStore.timeOffService.createEntry(slug, data);
      runInAction(() => {
        this.entries = [...this.entries, entry].sort(compareEntries);
        // Keep the server-driven settings table in sync without a refetch.
        this.pagedEntries = [...this.pagedEntries, entry].sort(compareEntries);
        this.pageInfo = { ...this.pageInfo, totalCount: this.pageInfo.totalCount + 1 };
      });
      // The name stays (NG-2060). It gains the id and the surface.
      track("time_off_created", { time_off_id: entry.id, source });
      return entry;
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  }

  /** The "My" writes are the My Time Off panel's, so it is their default surface. */
  async createMyEntry(
    slug: string,
    data: ITimeOffEntryPayload,
    source: AnalyticsSource = "side_panel",
  ): Promise<ITimeOffEntry> {
    this.isSaving = true;
    try {
      const entry = await this.rootStore.timeOffService.createMyEntry(slug, data);
      runInAction(() => {
        this.entries = [...this.entries, entry].sort(compareEntries);
      });
      track("time_off_created", { time_off_id: entry.id, source });
      return entry;
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  }

  async updateEntry(
    slug: string,
    entryId: string,
    data: Partial<ITimeOffEntryPayload>,
    source: AnalyticsSource = "page",
  ): Promise<ITimeOffEntry> {
    // Read before the write replaces it: `fields` is what this save changed.
    const fields = changedFields(data, this.beforeUpdate(entryId));
    this.activeEntryId = entryId;
    try {
      const entry = await this.rootStore.timeOffService.updateEntry(slug, entryId, data);
      runInAction(() => {
        const apply = (item: ITimeOffEntry) => (item.id === entryId ? entry : item);
        this.entries = this.entries.map(apply).sort(compareEntries);
        this.pagedEntries = this.pagedEntries.map(apply).sort(compareEntries);
      });
      if (fields.length > 0) track("time_off_updated", { time_off_id: entryId, fields, source });
      return entry;
    } finally {
      runInAction(() => {
        this.activeEntryId = null;
      });
    }
  }

  async updateMyEntry(
    slug: string,
    entryId: string,
    data: Partial<ITimeOffEntryPayload>,
    source: AnalyticsSource = "side_panel",
  ): Promise<ITimeOffEntry> {
    const fields = changedFields(data, this.beforeUpdate(entryId));
    this.activeEntryId = entryId;
    try {
      const entry = await this.rootStore.timeOffService.updateMyEntry(slug, entryId, data);
      runInAction(() => {
        this.entries = this.entries
          .map((item) => (item.id === entryId ? entry : item))
          .sort(compareEntries);
      });
      if (fields.length > 0) track("time_off_updated", { time_off_id: entryId, fields, source });
      return entry;
    } finally {
      runInAction(() => {
        this.activeEntryId = null;
      });
    }
  }

  async deleteEntry(
    slug: string,
    entryId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    this.activeEntryId = entryId;
    try {
      await this.rootStore.timeOffService.deleteEntry(slug, entryId);
      runInAction(() => {
        this.entries = this.entries.filter((entry) => entry.id !== entryId);
        const before = this.pagedEntries.length;
        this.pagedEntries = this.pagedEntries.filter((entry) => entry.id !== entryId);
        if (this.pagedEntries.length < before) {
          this.pageInfo = {
            ...this.pageInfo,
            totalCount: Math.max(0, this.pageInfo.totalCount - 1),
          };
        }
      });
      track("time_off_deleted", { time_off_id: entryId, source });
    } finally {
      runInAction(() => {
        this.activeEntryId = null;
      });
    }
  }

  async deleteMyEntry(
    slug: string,
    entryId: string,
    source: AnalyticsSource = "side_panel",
  ): Promise<void> {
    this.activeEntryId = entryId;
    try {
      await this.rootStore.timeOffService.deleteMyEntry(slug, entryId);
      runInAction(() => {
        this.entries = this.entries.filter((entry) => entry.id !== entryId);
      });
      track("time_off_deleted", { time_off_id: entryId, source });
    } finally {
      runInAction(() => {
        this.activeEntryId = null;
      });
    }
  }
}

function compareEntries(a: ITimeOffEntry, b: ITimeOffEntry): number {
  return (
    a.start_date.localeCompare(b.start_date) ||
    a.end_date.localeCompare(b.end_date) ||
    a.user.display_name.localeCompare(b.user.display_name) ||
    a.title.localeCompare(b.title)
  );
}
